import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, AlertTitle, Box, Button, CircularProgress } from '@mui/material';
import React, {
    ForwardedRef,
    useEffect,
    useImperativeHandle,
    useMemo,
} from 'react';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import ConnectForm from './ConnectForm';
import { SmartFormProps } from './RouteWizard';
import { useWizard } from './Wizard';

function Form(
  {
    schema,
    defaultValues,
    children,
    onSubmit,
    formId,
    sx,
    isLoading = false,
    submitText = 'Create',
    showControls = true,
    syncLeaveWarningWithDirty = false,
    validationMode,
    disableSubmitUntilValidChange = false,
  }: SmartFormProps,
  ref: ForwardedRef<UseFormReturn>,
) {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    shouldUnregister: false,
    mode: validationMode,
  })
  useImperativeHandle(ref, () => methods)
  const wizardData = useWizard()
  const { setLeaveWarning } = wizardData

  const history = useHistory()
  const {
    formState: { errors, isDirty, isValid },
  } = methods
  const isSubmitDisabled =
    wizardData?.loading ||
    (disableSubmitUntilValidChange && (!isDirty || !isValid))

  useEffect(() => {
    console.log('Form errors ', errors)
  }, [errors])

  const errorMessages = useMemo(() => {
    const fieldLabels: Record<string, string> = {
      mainPassenger: 'Lead passenger',
      contactNumber: 'Contact number',
      clientId: 'Client',
      onBehalfOf: 'On behalf of',
      bookingReferenceValues: 'Account references',
      passengers: 'Passengers',
      'outbound.pickup': 'Pickup location',
      'outbound.dropOff': 'Drop-off location',
      'outbound.when': 'Pickup time',
      'inbound.when': 'Return time',
    }

    const humaniseSegment = (seg: string) =>
      seg
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .trim()
        .toLowerCase()

    const prettifyPath = (path: string): string => {
      if (!path) return ''
      if (fieldLabels[path]) return fieldLabels[path]
      const segments = path.split('.').filter((s) => !/^\d+$/.test(s))
      const labelled = segments.map(
        (seg) => fieldLabels[seg] || humaniseSegment(seg),
      )
      const label = labelled.join(' - ')
      return label.charAt(0).toUpperCase() + label.slice(1)
    }

    const prettifyMessage = (msg: string) => {
      const trimmed = msg.trim().replace(/\s+/g, ' ')
      if (!trimmed) return trimmed
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    }

    const messages: string[] = []
    const seen = new Set<string>()
    const walk = (node: any, path: string) => {
      if (!node || typeof node !== 'object') return
      if (typeof node.message === 'string' && node.message.length > 0) {
        const label = prettifyPath(path)
        const msg = prettifyMessage(node.message)
        const line = label ? `${label}: ${msg}` : msg
        if (!seen.has(line)) {
          seen.add(line)
          messages.push(line)
        }
        return
      }
      for (const key of Object.keys(node)) {
        if (key === 'ref' || key === 'type') continue
        walk(node[key], path ? `${path}.${key}` : key)
      }
    }
    walk(errors, '')
    return messages
  }, [errors])

  useEffect(() => {
    if (!syncLeaveWarningWithDirty) {
      return
    }

    setLeaveWarning(methods.formState.isDirty)
  }, [methods.formState.isDirty, setLeaveWarning, syncLeaveWarningWithDirty])

  useEffect(() => {
    if (!syncLeaveWarningWithDirty) {
      return
    }

    return () => {
      setLeaveWarning(undefined)
    }
  }, [setLeaveWarning, syncLeaveWarningWithDirty])

  return (
    <FormProvider {...methods}>
      <Box
        component={'form'}
        onSubmit={methods.handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: '20px',
          marginBottom: '30px',
          // maxWidth: '750px',
          ...sx,
        }}
        id={formId}
      >
        {errorMessages.length > 0 && (
          <Alert severity="error">
            <AlertTitle>Please fix the following before continuing</AlertTitle>
            <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
              {errorMessages.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </Box>
          </Alert>
        )}
        <ConnectForm>{children}</ConnectForm>
        {showControls && (
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: wizardData?.hasPreviousStep
                ? 'space-between'
                : 'flex-end',
            }}
          >
            {wizardData?.hasPreviousStep && (
              <Button
                onClick={() => history.goBack()}
                disabled={wizardData?.loading}
              >
                Back
              </Button>
            )}
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box>
                <Button type="submit" disabled={isSubmitDisabled}>
                  {wizardData?.hasNextStep ? 'Next' : submitText}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </FormProvider>
  )
}

export default React.forwardRef(Form)
